import { Controller } from "@hotwired/stimulus"

// Generic single-page wizard. Lives in DOM as a stack of `data-wizard-target="pane"`
// sections; only one is visible at a time. The matching `stepper` cells light
// up as the user advances. Validation per pane uses native HTML5
// `checkValidity()` on the inputs inside the visible pane only — the form's
// final submit hits Rails as usual once step 3 is reached.
//
// Wired by `<form data-controller="wizard">` plus elements:
//   data-wizard-target="pane"  data-step="1|2|3"
//   data-wizard-target="stepper-step" data-step="1|2|3"
//   data-action="click->wizard#next"
//   data-action="click->wizard#prev"
//
// State: this.currentStep (1-based). On connect we show step 1, the rest stay
// hidden via the hidden attribute (set in markup).
export default class extends Controller {
  static targets = ["pane", "stepperStep", "nextBtn", "prevBtn", "submitBtn", "progress", "counter"]
  static values  = { current: { type: Number, default: 1 } }

  connect() {
    this._sync()
  }

  next(event) {
    event?.preventDefault?.()
    if (!this._validateCurrentPane()) return
    if (this.currentValue >= this.paneTargets.length) return
    this.currentValue = this.currentValue + 1
  }

  prev(event) {
    event?.preventDefault?.()
    if (this.currentValue <= 1) return
    this.currentValue = this.currentValue - 1
  }

  goto(event) {
    event?.preventDefault?.()
    const target = parseInt(event.currentTarget.dataset.step, 10)
    if (!target || isNaN(target)) return
    // Only allow jumping backwards or to an already-validated step.
    if (target < this.currentValue) {
      this.currentValue = target
    } else if (target === this.currentValue) {
      return
    } else {
      // Validate every pane between current and target.
      for (let i = this.currentValue; i < target; i++) {
        if (!this._validatePane(i)) {
          this.currentValue = i
          return
        }
      }
      this.currentValue = target
    }
  }

  currentValueChanged() {
    this._sync()
  }

  _sync() {
    this.paneTargets.forEach((pane) => {
      const step = parseInt(pane.dataset.step, 10)
      const visible = step === this.currentValue
      pane.hidden = !visible
      // Inputs inside hidden panes shouldn't block submit validation. Disable
      // them at the validation level by removing required, then restore on
      // re-show. Browsers refuse to submit a form with an invisible required
      // input that fails — without this the user can't reach step 3 if step
      // 1's radio isn't picked.
      pane.querySelectorAll("[required]").forEach((el) => {
        if (!visible) {
          el.dataset.wasRequired = "true"
          el.removeAttribute("required")
        } else if (el.dataset.wasRequired) {
          el.setAttribute("required", "true")
        }
      })
    })

    if (this.hasStepperStepTarget) {
      this.stepperStepTargets.forEach((cell) => {
        const step = parseInt(cell.dataset.step, 10)
        cell.classList.toggle("is-active", step === this.currentValue)
        cell.classList.toggle("is-done",   step <  this.currentValue)
      })
    }
    if (this.hasCounterTarget) this.counterTarget.textContent = String(this.currentValue)

    const last = this.paneTargets.length
    if (this.hasNextBtnTarget)   this.nextBtnTarget.hidden   = this.currentValue >= last
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.hidden = this.currentValue <  last
    if (this.hasPrevBtnTarget)   this.prevBtnTarget.hidden   = this.currentValue <= 1
    if (this.hasProgressTarget) {
      const pct = ((this.currentValue - 1) / (last - 1)) * 100
      this.progressTarget.style.width = `${pct}%`
    }
  }

  _validateCurrentPane() {
    return this._validatePane(this.currentValue)
  }

  _validatePane(stepNum) {
    const pane = this.paneTargets.find(p => parseInt(p.dataset.step, 10) === stepNum)
    if (!pane) return true
    // Re-apply `required` (if removed by _sync) before validating; then
    // checkValidity will report the missing-input correctly.
    const reqs = pane.querySelectorAll("[data-was-required], [required]")
    let ok = true
    reqs.forEach((el) => {
      if (el.dataset.wasRequired) el.setAttribute("required", "true")
      if (!el.checkValidity()) {
        ok = false
        el.reportValidity?.()
      }
    })
    return ok
  }
}
